import https from 'https';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import jwtDecode from 'jwt-decode';

export default (context, inject) => {
  const axiosApi = createAxiosInstance(context);
  const api = async (path, requestParams = {}, formData = null) => {
    let opts = { httpsAgent: new https.Agent({ rejectUnauthorized: false }), headers: {} };

    const tokenInfo = context.store.getters['utils/getApiTokens'] || context.$cookies.get('bonkers-ie-api-token');
    if (tokenInfo) {
      opts.headers = {
        Authorization: `Bearer ${tokenInfo.access_token}`,
        'X-Client-Request': context.route.path,
        'X-Requested-With': 'XMLHttpRequest'
      };
    }

    if (process.server && context.req && context.req.headers && context.req.headers['x-forwarded-for']) {
      opts.headers['X-Client-IP'] = context.req.headers['x-forwarded-for'];
    }
    path = path.replace(/^\/|\/$/g, '');
    let { method, headers, ...params } = requestParams;
    const url = generateUrl(context, path);
    const clientRoute = context.route ? context.route.fullPath : '-';
    method = method || 'get';
    context.$honeybadgerGeneric.addBreadcrumb(`API ${method.toUpperCase()}`, {
      category: 'request', metadata: { clientRoute, params, url }
    });

    if (headers) {
      for (let key in headers) {
        opts.headers[key] = headers[key];
      }
    }

    if (method === 'get') {
      if (path.match(/sessions\/tokens/)) {
        opts.withCredentials = true;
        const sessionCookie = context.$cookies.get('_bonkers_session');
        if (sessionCookie) {
          opts.headers['Cookie'] = `_bonkers_session=${context.$cookies.get('_bonkers_session')}`;
        }
      }
      requestCount(1);
      let response = await axiosApi.get(url, { ...opts, params }).catch(e => {
        context.$honeybadgerGeneric.addBreadcrumb('API Request Error', {
          category: 'request', metadata: { url, params, method, headers, clientRoute, opts }
        });
        throw e;
      });
      requestCount(-1);
      if (response) {
        context.$honeybadgerGeneric.addBreadcrumb('API Response Status', {
          category: 'request', metadata: { status: response.status }
        });
        return context.$deserialize(response.data);
      }
      return null;
    } else if (method === 'delete') {
      requestCount(1);
      let response = await axiosApi.delete(url, { ...opts, params }).then(r => {
        requestCount(-1);
        return r;
      });
      return context.$deserialize(response.data);
    } else {
      requestCount(1);
      let response;
      if (path.match(/users\/account/) && params.user && params.user.profile_image) {
        response = await uploadProfileImageRequest(params, method, url, opts);
      } else {
        const p = formData || params;
        response = await axiosApi[method](url, p, opts).then(r => {
          requestCount(-1);
          return r;
        }).catch(e => {
          context.$honeybadgerGeneric.addBreadcrumb('API Error', {
            category: 'request', metadata: { url, params, method, headers, clientRoute, opts }
          });
          throw e;
        });
      }
      if (response && response.data) {
        return context.$deserialize(response.data);
      }
      return null;
    }
  };

  const uploadProfileImageRequest = async (params, method, url, opts) => {
    let formData = new FormData();
    formData.append('user[profile_image]', params.user.profile_image);
    opts.headers['Content-Type'] = 'multipart/form-data';
    return axiosApi[method](url, formData, opts).then(r => {
      requestCount(-1);
      return r;
    });
  };

  inject('api', api);
  context.$api = api;
};

const createAxiosInstance = (context) => {
  const axiosInstance = context.$axios.create({ maxRedirects: 0 });

  // handle refresh of tokens and retry of failed requests
  const refreshAuthLogic = async failedRequest => {
    const token = await refreshAccessToken(context, axiosInstance);
    if (token) {
      failedRequest.response.config.headers['Authorization'] = 'Bearer ' + token;
      return Promise.resolve();
    } else {
      return Promise.reject(failedRequest);
    }
  };

  // handle refresh token expired
  axiosInstance.interceptors.response.use(
    response => response,
    error => {
      if (error.response && error.response.status) {
        if (error.response.status === 401 && error.response.config.url.match(/sessions\/refresh/)) {
          context.$cookies.remove('bonkers-ie-api-token');
          context.store.commit('utils/setApiTokens', null);
          let basePath = context.store.getters['products/getBasePath'] || '/';
          return context.redirect(context.app.localePath(basePath), {
            flashClass: 'error',
            flashMessage: context.store.$i18n.t('errors.apiSessionExpired')
          });
        }
        if (error.response.status === 404 && error.response.data && Object.keys(error.response.data).includes('path')) {
          if (error.response.data.path) {
            context.redirect(301, error.response.data.path);
            return Promise.reject(error);
          }
        }
        if (error.response.status === 400 && error.response.data && Object.keys(error.response.data).includes('form_errors')) {
          context.store.commit('utils/setFormErrors', error.response.data.form_errors);
        }
      }
      return Promise.reject(error);
    }
  );
  createAuthRefreshInterceptor(axiosInstance, refreshAuthLogic);
  return axiosInstance;
};

const refreshAccessToken = async (context, axiosInstance) => {
  const tokenInfo = context.store.getters['utils/getApiTokens'];
  const url = generateUrl(context, 'util/sessions/refresh');

  try {
    const response = await axiosInstance.post(url, {
      skipAuthRefresh: true,
      token: tokenInfo.refresh_token
    });

    if (response.status === 200) {
      const data = context.$deserialize(response.data);
      delete data.id;
      const decodedToken = jwtDecode(data.access_token);
      const { payload: { user_id: userId } } = decodedToken;
      if (userId === 'guest') context.store.commit('user/set', null);
      context.$cookies.set('bonkers-ie-api-token', data);
      context.store.commit('utils/setApiTokens', data);
      return data.access_token;
    }
  } catch (e) {}
};

const generateUrl = (context, path) => {
  return encodeURI(`${context.$railsHost()}/api/app/${path}/`);
};

const requestCount = (dir) => {
  if (typeof window !== 'undefined') {
    if (!window.requestCount) {
      window.requestCount = 0;
    }
    window.requestCount += dir;
  }
};
